import React, { useState, useEffect } from 'react';
import { IonCol, IonButton, IonGrid, IonRow, IonToast, IonLoading, IonImg } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { APP_CONFIG } from '../../constants';

// Validators
import { ScanValidator, AppValidator } from './../../validators';

// Helpers
import { savePageInit, isNative, resolveColorMessageByStatusCode, sendEventQrPage, hasAccess } from '../../helpers';

// Actions
import { resetErrorAction, startListAvailableBenefitsAction, resetTransactionAction, setEntityAction, startCreateTransactionAction, resetQrAction, resetBenefitAction, setQrAction, setErrorAction } from './../../redux';

// scss
import './QrScanComponent.scss';

export const QrScanComponent = ({
    pageConfig,
    history,
    userPermission
}) => {

    const dispatch = useDispatch();
    const { transaction } = useSelector(state => state.qr);
    const { activated: benefitSelected, list: benefits } = useSelector(state => state.benefit);
    const { error } = useSelector(state => state.app);
    const [showToast, setShowToast] = useState({ show: false, error: null, status: null });
    const [loading, setLoading] = useState(false);
    const hasPermissions = hasAccess(userPermission, pageConfig.permissions);
    const mainteinment = AppValidator.AppIsMainteinment();

    /**
     * Al cargar la página de QR, se la setea como página inicial
     */
    useEffect(() => {
        savePageInit(APP_CONFIG.PAGES.QR_SCAN.path);
        // Inicializa el objeto que se utiliza para intercambiar info con las apps
        if(! window.WebView) window.WebView = {};
        window.WebView.setScannedData = setScannedData;

    }, []);

    /**
     * Cuando se escanea la información del QR, se valida si el mismo está expirado
     * En caso de no estar, se resuelve si se redirecciona a la página de selección de beneficios (flujo sin beneficio)
     * o a la de detalle (flujo con beneficio)
     * Para el flujo sin beneficio se llama a la API para consultar los beneficios
     * Para el flujo con beneficio, se llama a la API para crear la transacción
     */
    useEffect(() => {
        if (transaction.transactionId !== '' && transaction.expiredIn !== undefined) {

            const expired = ScanValidator.validateTimeExpired(transaction.expiredIn);
            if (!expired) {

                // Elimino el tiempo de expiración para no mandárselo a la API
                if (transaction?.expiredIn) {
                    delete transaction['expiredIn'];
                }

                // Si lo escaneado contiene el beneficio, crea transacción y envía al detalle
                if (ScanValidator.validateExistPropBenefitId(transaction)) {
                    dispatch(startCreateTransactionAction(transaction, pageConfig, setLoading, history));
                    dispatch(resetQrAction());

                    // Analytics
                    sendEventQrPage();
                }
                // sino contiene el beneficio, redirige a la selección del beneficio
                else {
                    dispatch(startListAvailableBenefitsAction(transaction, setLoading));
                }
            }
        }

    }, [transaction]);

    /**
     * En caso de ocurrir cualquier error al llamar a la API o al escanear; muestra el mensaje
     */
    useEffect(() => {

        if (error) {
            setShowToast({ show: true, error: error.message, status: error.status });
            dispatch(resetTransactionAction());
            // dispatch(resetErrorAction());
            dispatch(resetQrAction());
            dispatch(resetBenefitAction());
        }

    }, [error]);

    /**
     * Si ya se han consultado los beneficios disponibles para este subscriptor:
     * En caso que haya un sólo beneficio disponible, se lo redirige al detalle
     * En caso que haya más de uno, se lo redirige a la página de beneficios
     */
    useEffect(() => {

        if (benefits.length > 0) {

            const entity = {

                ...transaction,
                benefitId: benefitSelected.benefitId
            };

            dispatch(setEntityAction(entity));

            // Si esto está vacío, significa que tengo más de un beneficio por seleccionar
            if (entity.benefitId !== '') {

                dispatch(resetBenefitAction());
                dispatch(startCreateTransactionAction(entity, pageConfig, setLoading, history));
            }
            else {
                history.push(history.push(APP_CONFIG.PAGES.BENEFIT.path));
            }

            // Analytics
            sendEventQrPage();
        }

    }, [benefits]);

    /**
     * Maneja el escaneo desde el dispositivo (sólo para mobile)
     */
    const handleScanCode = async () => {
        if (isNative()) {
            //limpiamos la información previamente cargada para prevenir leer información vieja.
            window.WebView.scannedData = undefined;
            window.WebView.onClickQR();
        }
        else {
            const err = { message: pageConfig.messages.error.notFunctionWeb, status: null };
            dispatch(setErrorAction(err));
        }
    };

    /**
     * Función que disponibilizamos en windows para que la invoquen las apps una vez que escanean el código QR
     * @param {Object} scannedData 
     */
    const setScannedData = (scannedData) => {
        window?.DD_LOGS?.logger && window.DD_LOGS.logger.info({scannedData});
        dispatch(setQrAction(scannedData));
    }

    return (
        <>
            <IonLoading
                isOpen={loading}
                message={APP_CONFIG.CONTROLS.LOADING.MESSAGE}
            />
             <IonToast
                isOpen={showToast.show}
                onDidDismiss={() => {setShowToast(false); dispatch(resetErrorAction());}}
                message={showToast.error}
                duration={APP_CONFIG.CONTROLS.TOAST.DURATION}
                position={APP_CONFIG.CONTROLS.TOAST.POSITION}
                color="danger"
            />
            <IonGrid className="animate__animated animate__fadeIn slow">
                <IonRow className="grid-rows-3 state--msg">
                    <IonCol>
                        <p className="subtitle"> Escaneá el código QR generado por el socio de CLN</p>
                    </IonCol>
                    <IonCol className="text-align-center">
                        <IonImg className="illustration" src="assets/images/qr-scan.png" />
                    </IonCol>
                    <IonCol className="dispFlex-justCenter">
                        <IonButton
                            type="button"
                            shape="round"
                            size="undefined"
                            disabled={!hasPermissions || mainteinment.enabled}
                            onClick={handleScanCode}>
                            ESCANEAR QR
                        </IonButton>
                    </IonCol>
                </IonRow>
            </IonGrid >
        </>
    )
}
